import { Schema } from "effect"
import { AgentPatch, AreaPatch, GoalPatch, InitiativePatch, KpiPatch, TaskPatch } from "./commands/graph"
import { CommandRejection, EventRef } from "./commands/index"
import { Actor, EntityRef, Evidence, Hold, Principal, RankedRef, Taint } from "./common"
import { Monitor, WakeReason } from "./domain/agentlife"
import { Intake, IntakeStatus } from "./domain/intake"
import { Artifact, Comment, MemoryItem } from "./domain/knowledge"
import { Agent, Area, Charter, IntegrationGrant, Mind, Policy, PolicyDocument } from "./domain/org"
import {
  BetVerdictValue,
  Goal,
  Initiative,
  InitiativeStatus,
  Kpi,
  KpiSample,
  MetricCondition,
} from "./domain/plan"
import { Proposal, ProposalBody } from "./domain/proposal"
import { BlockReason, DoneCheck, Edge, Review, ReviewStage, Task, TaskStatus } from "./domain/task"
import { Integration, Member, ModelCredential, Project, Repo } from "./domain/workspace"
import {
  AgentId,
  AreaId,
  ArtifactId,
  ChangesetId,
  CommandId,
  CommentId,
  CredentialId,
  EdgeId,
  Epoch,
  GoalId,
  InitiativeId,
  IntakeId,
  IntegrationId,
  KpiId,
  MemoryItemId,
  Millis,
  MonitorId,
  PolicyId,
  ProfileId,
  ProjectId,
  ProposalId,
  RepoId,
  RunId,
  RunnerId,
  Seq,
  Sha256,
  TaskId,
  UserId,
  WakeId,
} from "./ids"
import { Unmet } from "./placement"
import {
  Downgrade,
  Executor,
  Handoff,
  Run,
  RunError,
  RunOutcome,
  SessionRef,
  StopReason,
  Usage,
  Verdict,
} from "./run"
import { RunnerAdminState, RunnerManifest, RunnerRecord } from "./runner"

// #region event-meta
/** Which policy revision allowed an event. Rolling a policy back never rewrites what was allowed at the time. */
export const PolicyStamp = Schema.Struct({
  policyId: Schema.NullOr(PolicyId),
  revision: Schema.Int,
  verdict: Schema.Literals(["allow", "propose", "exempt"]), // exempt: people, and parts of an approved proposal
  rule: Schema.NullOr(Schema.String), // e.g. "allow.create_task"
})

/** Shared by every log. Every event explains itself: who, why, as part of what, under which rule, approved by whom, how trustworthy. */
export const EventMeta = Schema.Struct({
  seq: Seq,
  ts: Millis, // stamped by the owning DO at append
  v: Schema.Int, // payload schema version for this _tag; upcasters key on (_tag, v)
  actor: Actor,
  commandId: Schema.NullOr(CommandId),
  causedBy: Schema.NullOr(Schema.Union([CommandId, EventRef])),
  links: Schema.Array(EventRef),
  correlationId: Schema.String,
  changesetId: Schema.NullOr(ChangesetId),
  policy: Schema.NullOr(PolicyStamp),
  approvedBy: Schema.NullOr(Schema.Struct({ proposalId: ProposalId, by: Actor })),
  taint: Taint, // the actor's run taint at append time; projections render tainted free text as quoted data
})
// #endregion event-meta

// #region project-events
const before = <S extends Schema.Top>(s: S) => s // readability marker: "the previous value", for cheap inverses

/** How a task got to its new status. One event type with a typed cause keeps evolve trivial. */
export const StatusCause = Schema.TaggedUnion({
  attempt_started: { runId: RunId },
  attempt_ended: {
    runId: RunId,
    stopReason: StopReason,
    outcome: Schema.String,
    retry: Schema.NullOr(Schema.Struct({ at: Millis, pinProfile: Schema.NullOr(ProfileId) })),
  },
  review: {
    round: Schema.Int,
    stage: ReviewStage,
    verdict: Schema.Literals(["approve", "changes_requested", "pass", "fail", "inconclusive"]),
  },
  blocked: {},
  unblocked: { note: Schema.String },
  retry: { note: Schema.NullOr(Schema.String) },
  completed: { evidence: Schema.Array(Evidence) },
  impossible_accepted: { note: Schema.String },
  cancelled: { reason: Schema.String },
  reopened: { reason: Schema.String },
  /** A CompleteTask moved a `todo` or `blocked` task into review (decision 0032). */
  closed: { note: Schema.String, evidence: Schema.Array(Evidence) },
})

export const ProjectEvent = Schema.TaggedUnion({
  // ---- genesis, settings, sync ----
  ProjectCreated: { project: Project },
  ProjectUpdated: { patch: Schema.JsonObject, before: Schema.JsonObject },
  WorkspaceSynced: {
    members: Schema.Array(Member),
    mayor: Schema.NullOr(Schema.Struct({ agentId: AgentId, ownerId: UserId })),
  },
  PolicyCreated: { policy: Policy },
  PolicyRevised: {
    policyId: PolicyId,
    revision: Schema.Int,
    document: PolicyDocument,
    restoredFrom: Schema.NullOr(Schema.Int),
    before: before(PolicyDocument),
  },
  // ---- areas and agents ----
  AreaCreated: { area: Area },
  AreaUpdated: { areaId: AreaId, patch: AreaPatch, before: before(AreaPatch) },
  AreaCharterRevised: { areaId: AreaId, charter: Charter, before: before(Charter) },
  AreaArchived: { areaId: AreaId },
  IntegrationGranted: { areaId: AreaId, grant: IntegrationGrant },
  IntegrationRevoked: { areaId: AreaId, integrationId: IntegrationId },
  AgentHired: { agent: Agent },
  AgentUpdated: { agentId: AgentId, patch: AgentPatch, before: before(AgentPatch) },
  AgentCharterRevised: { agentId: AgentId, charter: Charter, before: before(Charter) },
  AgentMindChanged: { agentId: AgentId, mind: Mind, before: before(Mind) },
  AgentStatusChanged: {
    agentId: AgentId,
    status: Schema.Literals(["active", "paused", "retired"]),
    before: Schema.Literals(["active", "paused", "retired"]),
  },
  // ---- goals and KPIs ----
  GoalCreated: { goal: Goal },
  GoalUpdated: { goalId: GoalId, patch: GoalPatch, before: before(GoalPatch) },
  GoalStatusChanged: {
    goalId: GoalId,
    status: Schema.Literals(["active", "achieved", "missed", "abandoned"]),
    before: Schema.Literals(["active", "achieved", "missed", "abandoned"]),
    note: Schema.String,
  },
  TargetChanged: {
    subject: Schema.TaggedUnion({ goal: { id: GoalId }, initiative: { id: InitiativeId } }),
    condition: MetricCondition,
    before: Schema.NullOr(MetricCondition),
  },
  KpiDefined: { kpi: Kpi },
  KpiUpdated: { kpiId: KpiId, patch: KpiPatch, before: before(KpiPatch) },
  KpiSampleRecorded: { kpiId: KpiId, sample: KpiSample },
  // ---- initiatives ----
  InitiativeCreated: { initiative: Initiative },
  InitiativeUpdated: { initiativeId: InitiativeId, patch: InitiativePatch, before: before(InitiativePatch) },
  InitiativeStatusChanged: {
    initiativeId: InitiativeId,
    status: InitiativeStatus,
    before: InitiativeStatus,
    reason: Schema.NullOr(Schema.String),
    workDone: Schema.Boolean,
  },
  BetVerdictRecorded: {
    initiativeId: InitiativeId,
    value: BetVerdictValue,
    by: AgentId,
    evidence: Schema.Array(Evidence),
  },
  // ---- tasks ----
  /** Carries the ancestry snapshot so the event never needs later state to make sense. */
  TaskCreated: {
    task: Task,
    ancestry: Schema.Struct({ goalIds: Schema.Array(GoalId), initiativeId: InitiativeId, areaId: AreaId }),
  },
  TaskUpdated: { taskId: TaskId, patch: TaskPatch, before: before(TaskPatch) },
  ContractRevised: {
    taskId: TaskId,
    checks: Schema.Array(DoneCheck),
    review: Review,
    before: Schema.Struct({ checks: Schema.Array(DoneCheck), review: Review }),
  },
  TaskAssigned: { taskId: TaskId, assigneeId: Schema.NullOr(AgentId), before: Schema.NullOr(AgentId) },
  TaskStatusChanged: {
    taskId: TaskId,
    from: TaskStatus,
    to: TaskStatus,
    cause: StatusCause,
    blocked: Schema.NullOr(BlockReason),
  },
  /** `by` (decision 0032): who paused or resumed it; absent on events before the wave (derived from the event's actor). */
  TaskPaused: {
    taskId: TaskId,
    paused: Schema.Boolean,
    by: Schema.optionalKey(Schema.NullOr(Principal)),
    note: Schema.optionalKey(Schema.NullOr(Schema.String)),
  },
  RequestDecided: { taskId: TaskId, accepted: Schema.Boolean, note: Schema.NullOr(Schema.String) },
  ReviewRecorded: {
    taskId: TaskId,
    round: Schema.Int,
    stage: ReviewStage,
    verdict: Schema.Literals(["approve", "changes_requested", "pass", "fail", "inconclusive"]),
    notes: Schema.String,
    findings: Schema.Array(Schema.String),
    runId: Schema.NullOr(RunId),
  },
  RankChanged: { ref: RankedRef, rank: Schema.String, before: Schema.String },
  // ---- edges, holds, plan versions ----
  EdgeAdded: { edge: Edge },
  EdgeRemoved: { edgeId: EdgeId, edge: Edge }, // snapshot, so the inverse needs no lookup
  HoldSet: { ref: EntityRef, hold: Hold, before: Hold },
  /** Commit marker closing every changeset. Its events are contiguous and carry meta.changesetId. */
  ChangesetApplied: {
    changesetId: ChangesetId,
    planVersion: Schema.Int, // +1 per applied changeset; execution events never bump it
    rationale: Schema.String,
    firstSeq: Seq,
    changeCount: Schema.Int,
    proposalId: Schema.NullOr(ProposalId),
    revertOf: Schema.NullOr(Schema.Int),
    undoOf: Schema.NullOr(ChangesetId),
  },
  // ---- proposal ledger ----
  ProposalOpened: { proposal: Proposal },
  ProposalRevised: {
    proposalId: ProposalId,
    revision: Schema.Int,
    contentHash: Sha256,
    body: ProposalBody,
    reasoning: Schema.String,
  },
  ProposalRevisionRequested: { proposalId: ProposalId, revision: Schema.Int, note: Schema.String },
  ProposalApproved: {
    proposalId: ProposalId,
    revision: Schema.Int,
    selection: Schema.NullOr(Schema.Array(Schema.Int)),
  },
  ProposalApplied: {
    proposalId: ProposalId,
    revision: Schema.Int,
    changesetId: Schema.NullOr(ChangesetId),
    /** Permission proposals (settings wave §4.5): "run" = allow this tool for the rest of the run. */
    remember: Schema.optionalKey(Schema.Literals(["once", "run"])),
  },
  ProposalApplyFailed: { proposalId: ProposalId, revision: Schema.Int, rejection: CommandRejection },
  ProposalRejected: { proposalId: ProposalId, revision: Schema.Int, reason: Schema.NullOr(Schema.String) },
  ProposalAnswered: {
    proposalId: ProposalId,
    revision: Schema.Int,
    optionKey: Schema.NullOr(Schema.String),
    text: Schema.NullOr(Schema.String),
  },
  ProposalExpired: {
    proposalId: ProposalId,
    revision: Schema.Int,
    appliedDefault: Schema.NullOr(Schema.String),
  },
  ProposalWithdrawn: { proposalId: ProposalId },
  ProposalAdopted: { proposalId: ProposalId, changesetId: ChangesetId },
  // ---- comments, the agent bus, artifacts ----
  CommentPosted: { comment: Comment },
  CommentEdited: { commentId: CommentId, body: Schema.String, before: Schema.String },
  ThreadResolved: { threadId: CommentId },
  AskStateChanged: {
    askId: CommentId,
    state: Schema.Literals(["answered", "escalated"]),
    by: Schema.Union([CommentId, ProposalId]),
  },
  ArtifactPosted: { artifact: Artifact },
  ArtifactStateChanged: {
    artifactId: ArtifactId,
    state: Schema.Literals(["draft", "final", "published", "rejected"]),
    before: Schema.Literals(["draft", "final", "published", "rejected"]),
  },
  // ---- runs (milestones only: never chunks, heartbeats or renewals) ----
  /** `branch` (fix wave §4.2): the worktree branch of the frozen job, null = scratch; absent for turns and old events. */
  RunRequested: { run: Run, branch: Schema.optionalKey(Schema.NullOr(Schema.String)) },
  RunWaiting: { runId: RunId, unmet: Schema.Array(Unmet), since: Millis },
  RunLeased: { runId: RunId, epoch: Epoch, executor: Executor },
  /** The lease ended before the run reached "running": the hub offers it again (next epoch). */
  RunRequeued: {
    runId: RunId,
    epoch: Epoch,
    reason: Schema.Literals(["ceiling", "prepare_failed", "shutdown", "expired"]),
    prestartReleases: Schema.Int,
  },
  RunStarted: { runId: RunId, epoch: Epoch, sessionIn: Schema.NullOr(SessionRef), taint: Taint },
  RunMilestone: {
    runId: RunId,
    epoch: Epoch,
    kind: Schema.String,
    text: Schema.String,
    ref: Schema.NullOr(Schema.String),
  },
  RunOutcomeReported: { runId: RunId, epoch: Epoch, outcome: RunOutcome },
  RunVerdictReported: { runId: RunId, epoch: Epoch, verdict: Verdict },
  RunTaintRaised: { runId: RunId, epoch: Epoch, source: Schema.String },
  RunCancelRequested: { runId: RunId, reason: Schema.String },
  /** Truthful end: mechanical stop reason, the agent's claim, the accepted outcome, and any downgrade between them. */
  RunEnded: {
    runId: RunId,
    epoch: Epoch,
    stopReason: StopReason,
    claimed: Schema.NullOr(RunOutcome),
    outcome: RunOutcome,
    downgrade: Schema.NullOr(Downgrade),
    verdict: Schema.NullOr(Verdict),
    usage: Schema.Array(Usage),
    error: Schema.NullOr(RunError),
    sessionOut: Schema.NullOr(SessionRef),
    transcriptRef: Schema.NullOr(Schema.String),
  },
  OutwardActionExecuted: {
    proposalId: ProposalId,
    revision: Schema.Int,
    runId: RunId,
    resultRef: Schema.String,
  },
  OutwardActionFailed: { proposalId: ProposalId, revision: Schema.Int, runId: RunId, error: Schema.String },
  // ---- intake (Phase 2) ----
  IntakeCaptured: { intake: Intake },
  IntakeTriaged: {
    intakeId: IntakeId,
    suggestedAreaId: Schema.NullOr(AreaId),
    before: Schema.NullOr(AreaId),
  },
  /** linked, duplicate or closed: the item leaves the open intake list. */
  IntakeResolved: { intakeId: IntakeId, status: IntakeStatus, before: IntakeStatus },
  // ---- memory: project, area and task scopes ----
  MemoryItemCreated: { item: MemoryItem },
  MemoryItemRevised: { itemId: MemoryItemId, item: MemoryItem, beforeRevision: Schema.Int },
  MemoryItemArchived: { itemId: MemoryItemId, reason: Schema.String },
  MemoryItemRevoked: { itemId: MemoryItemId, reason: Schema.String }, // "show me every run that loaded it"
  // ---- watchdog, outcomes, privacy ----
  StallDetected: { subject: EntityRef, signal: Schema.String, proposalId: ProposalId },
  OutcomeCheckScheduled: {
    subject: EntityRef,
    dueAt: Millis,
    kpiIds: Schema.Array(KpiId),
    evaluator: AgentId,
  }, // Phase 3
  OutcomeCheckDue: { subject: EntityRef }, // Phase 3
  /**
   * The ONE sanctioned in-place change: the referenced row's fields are overwritten with a hash marker in the same
   * transaction. In state it rewrites `fields` of every record whose content was written at `seq` (`contentSeq`),
   * still last touched by it (`lastSeq`), or named in `refs` — the DO fills `refs` from the logged event at `seq`,
   * which covers patched graph nodes whose `lastSeq` has moved on since.
   */
  Redacted: {
    seq: Seq,
    fields: Schema.Array(Schema.String),
    reason: Schema.String,
    refs: Schema.optionalKey(Schema.Array(EntityRef)),
  },
})
export type ProjectEvent = typeof ProjectEvent.Type

export const ProjectEventEnvelope = Schema.Struct({ ...EventMeta.fields, event: ProjectEvent })
export type ProjectEventEnvelope = typeof ProjectEventEnvelope.Type
// #endregion project-events

// #region other-logs
/** Workspace DO log: durable transitions only. Heartbeats, renewals and latency are mutable rows. */
export const WorkspaceEvent = Schema.TaggedUnion({
  WorkspaceInitialised: { workspaceId: Schema.String, name: Schema.String, slug: Schema.String },
  MembersChanged: { members: Schema.Array(Member) },
  ProjectRegistered: { projectId: ProjectId, slug: Schema.String, name: Schema.String },
  ProjectArchived: { projectId: ProjectId },
  RepoRegistered: { repo: Repo },
  RepoRemoved: { repoId: RepoId },
  CredentialAdded: { credential: ModelCredential },
  CredentialRemoved: { credentialId: CredentialId },
  RunnerPaired: { runner: RunnerRecord },
  RunnerManifestChanged: { runnerId: RunnerId, manifest: RunnerManifest }, // on change only
  RunnerAdminChanged: { runnerId: RunnerId, admin: RunnerAdminState, before: RunnerAdminState },
  RunnerRenamed: { runnerId: RunnerId, name: Schema.String },
  LeaseGranted: {
    runId: RunId,
    projectId: Schema.NullOr(ProjectId),
    epoch: Epoch,
    runnerId: RunnerId,
    profileId: ProfileId,
    expiresAt: Millis,
  },
  LeaseEnded: {
    runId: RunId,
    epoch: Epoch,
    reason: Schema.Literals(["final", "released", "expired", "revoked", "stale"]),
    prestart: Schema.Boolean,
  },
  MayorHired: { agent: Agent },
  MayorUpdated: { agent: Agent },
  IntegrationRegistered: { integration: Integration }, // Phase 2
  IntegrationToolsRepinned: {
    integrationId: IntegrationId,
    tools: Schema.Array(Schema.Struct({ name: Schema.String, schemaHash: Sha256 })),
  },
  MemoryItemCreated: { item: MemoryItem },
  MemoryItemRevised: { itemId: MemoryItemId, item: MemoryItem, beforeRevision: Schema.Int },
  MemoryItemArchived: { itemId: MemoryItemId, reason: Schema.String },
  MemoryItemRevoked: { itemId: MemoryItemId, reason: Schema.String },
  // Mayor turns (workspace-level runs) reuse the Run* payloads of the project log.
  RunRequested: { run: Run },
  RunEnded: { runId: RunId, epoch: Epoch, stopReason: StopReason, usage: Schema.Array(Usage) },
})

/** Agent DO log (the body): inbox, turns, notebook, monitors, session continuity. The chat transcript itself is
 * AIChatAgent storage; native session entries are mirror rows, not events. */
export const AgentEvent = Schema.TaggedUnion({
  AgentConfigSynced: { agent: Agent }, // projection pushed by the Project DO (charter, mind, policy id, status)
  WakeReceived: { wakeId: WakeId, reason: WakeReason, key: Schema.String, cause: EventRef },
  WakeSkipped: {
    key: Schema.String,
    reason: Schema.Literals(["self_caused", "duplicate", "agent_paused", "agent_retired", "agent_mention"]),
  },
  TurnRequested: { runId: RunId, wakeBatchId: WakeId, wakeIds: Schema.Array(WakeId) },
  TurnStarted: { runId: RunId, executor: Executor },
  TurnSteered: {
    runId: RunId,
    messageId: Schema.String,
    priority: Schema.Literals(["now", "next", "later"]),
  },
  TurnInterruptRequested: { runId: RunId, by: UserId },
  TurnEnded: { runId: RunId, stopReason: StopReason, sessionOut: Schema.NullOr(SessionRef) },
  NotebookItemWritten: { item: MemoryItem }, // own notebook entries are auto-accepted
  NotebookItemArchived: { itemId: MemoryItemId, reason: Schema.String },
  MonitorSet: { monitor: Monitor },
  MonitorRemoved: { monitorId: MonitorId },
  MonitorFired: { monitorId: MonitorId, at: Millis, wakeId: WakeId },
  /** failure → blocked (the routine's turn failed); a person resumes it. */
  MonitorStateChanged: {
    monitorId: MonitorId,
    state: Schema.TaggedUnion({
      active: {},
      blocked: { reason: Schema.String, runId: Schema.NullOr(RunId) },
    }),
  },
  SessionRefUpdated: { ref: SessionRef },
  HandoffWritten: { runId: RunId, handoff: Handoff },
})

/** User DO log: personal memory and notification settings. Notifications and push subscriptions are rows. */
export const UserEvent = Schema.TaggedUnion({
  MemoryItemCreated: { item: MemoryItem },
  MemoryItemRevised: { itemId: MemoryItemId, item: MemoryItem, beforeRevision: Schema.Int },
  MemoryItemArchived: { itemId: MemoryItemId, reason: Schema.String },
  MemoryItemRevoked: { itemId: MemoryItemId, reason: Schema.String },
  NotificationPrefsChanged: {
    quietHours: Schema.NullOr(Schema.Struct({ from: Schema.String, to: Schema.String, tz: Schema.String })),
    soonBatchMinutes: Schema.Int,
    digestCron: Schema.NullOr(Schema.String),
  },
})
// #endregion other-logs
